import { useState, useEffect, useCallback } from "react";
import enhancedProfileApi from "../api/enhancedProfileApi";

export const useEnhancedProfile = (userId) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const profileData = await enhancedProfileApi.getCompleteProfile(userId);
      setProfile(profileData);
    } catch (err) {
      setError(err.message || "Failed to fetch profile data");
      console.error("Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateBasicProfile = useCallback(
    async (profileData) => {
      if (!userId) return;

      try {
        setUpdating(true);
        const updated = await enhancedProfileApi.updateBasicProfile(
          userId,
          profileData
        );
        setProfile((prev) => ({ ...prev, basic: updated }));

        // Re-fetch the complete profile to ensure all data is in sync
        await fetchProfile();

        return updated;
      } catch (err) {
        setError(err.message || "Failed to update profile");
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [userId, fetchProfile]
  );

  const updatePreferences = useCallback(
    async (preferences) => {
      if (!userId) return;

      try {
        setUpdating(true);
        const updated = await enhancedProfileApi.updatePreferences(
          userId,
          preferences
        );
        setProfile((prev) => ({ ...prev, preferences: updated }));
        return updated;
      } catch (err) {
        setError(err.message || "Failed to update preferences");
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [userId]
  );

  const updateSettings = useCallback(
    async (settings) => {
      if (!userId) return;

      try {
        setUpdating(true);
        const updated = await enhancedProfileApi.updateSettings(
          userId,
          settings
        );
        setProfile((prev) => ({ ...prev, settings: updated }));
        return updated;
      } catch (err) {
        setError(err.message || "Failed to update settings");
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [userId]
  );

  const uploadProfilePicture = useCallback(
    async (file) => {
      if (!userId) return;

      try {
        setUpdating(true);
        const result = await enhancedProfileApi.uploadProfilePicture(
          userId,
          file
        );
        setProfile((prev) => ({
          ...prev,
          basic: { ...prev.basic, profilePicture: result.profilePicture },
        }));
        return result;
      } catch (err) {
        setError(err.message || "Failed to upload profile picture");
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [userId]
  );

  const refreshProfile = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    updating,
    fetchProfile,
    refreshProfile,
    updateBasicProfile,
    updatePreferences,
    updateSettings,
    uploadProfilePicture,
  };
};

// Hook for specific profile sections with updates
export const useProfileSectionWithUpdate = (userId, section) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchSection = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      let result;
      switch (section) {
        case "basic":
          result = await enhancedProfileApi.getBasicProfile(userId);
          break;
        case "bookings":
          result = await enhancedProfileApi.getBookingStats(userId);
          break;
        case "payments":
          result = await enhancedProfileApi.getPaymentDetails(userId);
          break;
        case "preferences":
          result = await enhancedProfileApi.getPreferences(userId);
          break;
        case "subscription":
          result = await enhancedProfileApi.getSubscriptionInfo(userId);
          break;
        case "settings":
          result = await enhancedProfileApi.getSettings(userId);
          break;
        default:
          throw new Error("Invalid profile section");
      }

      setData(result);
    } catch (err) {
      setError(err.message || `Failed to fetch ${section} data`);
      console.error(`${section} fetch error:`, err);
    } finally {
      setLoading(false);
    }
  }, [userId, section]);

  const updateSection = useCallback(
    async (data) => {
      if (!userId) return;

      try {
        setUpdating(true);
        let updated;

        switch (section) {
          case "basic":
            updated = await enhancedProfileApi.updateBasicProfile(userId, data);
            break;
          case "preferences":
            updated = await enhancedProfileApi.updatePreferences(userId, data);
            break;
          case "settings":
            updated = await enhancedProfileApi.updateSettings(userId, data);
            break;
          default:
            throw new Error("This section cannot be updated");
        }

        setData(updated);
        return updated;
      } catch (err) {
        setError(err.message || `Failed to update ${section}`);
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [userId, section]
  );

  useEffect(() => {
    fetchSection();
  }, [fetchSection]);

  return {
    data,
    loading,
    error,
    updating,
    fetchSection,
    updateSection,
  };
};
